import { useState } from "react";
import {
  FileText,
  Image,
  File as FileIcon,
  Download,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatBytes } from "./AttachmentInput";

const getFileIcon = (mimeType = "") => {
  if (mimeType.startsWith("image/"))
    return (
      <div className="flex items-center justify-center w-8 h-8 rounded-md bg-blue-50 dark:bg-blue-950/40 flex-shrink-0">
        <Image className="h-4 w-4 text-blue-500" />
      </div>
    );
  if (mimeType === "application/pdf")
    return (
      <div className="flex items-center justify-center w-8 h-8 rounded-md bg-red-50 dark:bg-red-950/40 flex-shrink-0">
        <FileText className="h-4 w-4 text-red-500" />
      </div>
    );
  return (
    <div className="flex items-center justify-center w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-800 flex-shrink-0">
      <FileIcon className="h-4 w-4 text-gray-400" />
    </div>
  );
};

// ─── Single Row ────────────────────────────────────────────────────────────────
const AttachmentAnswerRow = ({ file, onDownload }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!onDownload || downloading) return;
    setDownloading(true);
    try {
      await onDownload(file);
    } finally {
      setDownloading(false);
    }
  };

  const mime = file?.mimeType ?? file?.type ?? "";

  return (
    <li
      className={cn(
        "flex items-center gap-3 px-3 py-2.5",
        "bg-white dark:bg-gray-900",
        "border border-gray-200 dark:border-gray-700 rounded-lg",
        "hover:bg-gray-50 dark:hover:bg-gray-800/60",
        "transition-colors",
      )}
    >
      {getFileIcon(mime)}

      <div className="flex-1 min-w-0">
        <p className="truncate text-sm font-medium text-gray-700 dark:text-gray-200">
          {file.name}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {formatBytes(file.size)}
        </p>
      </div>

      {onDownload && (
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          aria-label={`Download ${file.name}`}
          className={cn(
            "shrink-0 p-1.5 rounded-md transition-colors",
            downloading
              ? "text-gray-300 cursor-not-allowed dark:text-gray-600"
              : [
                  "text-gray-400 hover:text-emerald-600 hover:bg-emerald-50",
                  "dark:text-gray-500 dark:hover:text-emerald-400 dark:hover:bg-emerald-950/30",
                ],
          )}
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </button>
      )}
    </li>
  );
};
export const AttachmentAnswerList = ({ files = [], onDownload }) => {
  if (files.length === 0) {
    return (
      <span className="text-sm text-muted-foreground italic">
        No files attached
      </span>
    );
  }

  return (
    <div className="min-h-0">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        {files.length} file{files.length !== 1 ? "s" : ""} attached
      </p>
      <ul className="max-h-56 overflow-y-auto space-y-2 pr-1">
        {files.map((file, i) => (
          <AttachmentAnswerRow
            key={file.fileId ?? i}
            file={file}
            onDownload={onDownload}
          />
        ))}
      </ul>
    </div>
  );
};

export default AttachmentAnswerRow;
